import { useCallback, useEffect, useState } from 'react';
import * as servicesApi from '../api/memberServices';
import { useOrg } from '../lib/org';
import { useToast } from '../components/toast';
import { Spinner, ErrorAlert, EmptyState, TextareaField } from '../components/ui';
import ServiceRequestList from '../components/ServiceRequestList';
import { cn, ui } from '../lib/ui';
import { formatMoney } from '../lib/payments';
import { fieldErrorsFrom } from '../lib/formErrors';

function ServiceCard({ service, orgName, accredited, onRequested }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const needsAccreditation = service.requiresAccreditation && !accredited;

  async function submit(e) {
    e.preventDefault();
    if (details.trim().length < 10) {
      setError('Describe what you need in 10 to 4000 characters');
      return;
    }
    setSending(true);
    try {
      const data = await servicesApi.createServiceRequest(service.serviceId, details.trim());
      toast.success(
        data.request.status === 'pending_approval'
          ? 'Request sent to your organization for approval'
          : 'Request sent to the platform team'
      );
      setOpen(false);
      setDetails('');
      onRequested(data.request);
    } catch (err) {
      const fields = fieldErrorsFrom(err);
      setError(fields.details || fields.serviceId || null);
      toast.error(err.message || 'The request was not sent');
    } finally {
      setSending(false);
    }
  }

  return (
    <article className={cn(ui.card, 'flex flex-col')} aria-label={service.name}>
      <h3 className="mt-0 mb-1 text-title-s">{service.name}</h3>
      <p className="mt-0 mb-2 text-body-s">{service.description}</p>
      <p className={cn(ui.hint, 'mt-0')}>
        Flat fee of {formatMoney(service.feeCents)}, charged to {orgName} when the platform delivers
        it.
        {service.requiresAccreditation && ' For verified accredited investors.'}
      </p>
      {needsAccreditation ? (
        <p className={cn(ui.alertInfo, 'mb-0')}>
          Verify your accredited investor status under Verification to request this service.
        </p>
      ) : open ? (
        <form onSubmit={submit}>
          <TextareaField
            label="What do you need?"
            hint="For example the entity name and state, the people involved, and any deadline. Do not include account numbers or passwords."
            value={details}
            onChange={e => {
              setDetails(e.target.value);
              setError(null);
            }}
            rows={4}
            maxLength={4000}
            error={error}
          />
          <div className={ui.row}>
            <button className={ui.btnPrimary} type="submit" disabled={sending}>
              {sending ? 'Sending...' : 'Send request'}
            </button>
            <button className={ui.btnGhost} type="button" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="mt-auto">
          <button className={ui.btnSecondary} type="button" onClick={() => setOpen(true)}>
            Request {service.name.toLowerCase()}
          </button>
        </div>
      )}
    </article>
  );
}

/**
 * /services: investment member services (docs/Decisions.md D66). The
 * platform provides them and never handles members' funds or investments;
 * the organization pays a flat fee per delivered service, so a member's
 * request waits for an org admin's approval.
 */
export default function MemberServices() {
  const { activeOrg } = useOrg();
  const isOrgAdmin = activeOrg?.role === 'org-admin';
  const [catalog, setCatalog] = useState(null);
  const [mine, setMine] = useState([]);
  const [orgRequests, setOrgRequests] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [cat, own, org] = await Promise.all([
        servicesApi.getMemberServices(),
        servicesApi.listServiceRequests({ scope: 'mine' }),
        isOrgAdmin ? servicesApi.listServiceRequests({ scope: 'org' }) : Promise.resolve(null),
      ]);
      setCatalog(cat);
      setMine(own.requests || []);
      setOrgRequests(org?.requests || []);
    } catch (err) {
      setError(err);
    }
  }, [isOrgAdmin]);

  useEffect(() => {
    load();
  }, [load, activeOrg?.orgId]);

  const title = <h1 className={ui.h1}>Member services</h1>;
  if (!activeOrg) {
    return (
      <>
        {title}
        <EmptyState message="Choose an organization first." />
      </>
    );
  }
  if (error) {
    return (
      <>
        {title}
        <ErrorAlert error={error} />
      </>
    );
  }
  if (!catalog) {
    return (
      <>
        {title}
        <Spinner label="Loading services..." />
      </>
    );
  }

  const replace = list => updated =>
    list(prev => prev.map(r => (r.requestId === updated.requestId ? updated : r)));
  const pendingForAdmin = orgRequests.filter(r => r.status === 'pending_approval').length;
  // A platform admin's catalog includes services not offered yet; members only
  // ever see offered ones.
  const offered = catalog.services.filter(s => s.offered);

  return (
    <>
      {title}
      <p className={cn(ui.muted, 'mb-4')}>
        Services the platform provides to members of {activeOrg.name}, such as entity formation and
        cap table management. The platform never holds or moves your funds or investments. Each
        service has a flat fee that {activeOrg.name} pays.
      </p>

      {!catalog.eligibility.allowed ? (
        <div className={ui.alertInfo}>{catalog.eligibility.reason}</div>
      ) : offered.length === 0 ? (
        <EmptyState message="No services are offered yet. The platform team adds them as they become available." />
      ) : (
        <section aria-labelledby="services-heading" className="mb-8">
          <h2 id="services-heading" className={ui.h2}>
            Available services
          </h2>
          <div className={cn(ui.grid, ui.gridMd2)}>
            {offered.map(service => (
              <ServiceCard
                key={service.serviceId}
                service={service}
                orgName={activeOrg.name}
                accredited={catalog.accredited}
                onRequested={request => {
                  setMine(prev => [request, ...prev]);
                  if (isOrgAdmin) setOrgRequests(prev => [request, ...prev]);
                }}
              />
            ))}
          </div>
        </section>
      )}

      {isOrgAdmin && (
        <section aria-labelledby="org-requests-heading" className="mb-8">
          <h2 id="org-requests-heading" className={ui.h2}>
            Organization requests
            {pendingForAdmin > 0 && (
              <span className={cn(ui.pillWarning, 'ml-2 align-middle')}>
                {pendingForAdmin} to approve
              </span>
            )}
          </h2>
          <p className={cn(ui.hint, 'mb-3')}>
            Approving a request means {activeOrg.name} pays its fee once the platform delivers it.
          </p>
          <ServiceRequestList
            requests={orgRequests}
            empty="No member has asked for a service yet."
            showRequester
            onChanged={updated => {
              replace(setOrgRequests)(updated);
              replace(setMine)(updated);
            }}
          />
        </section>
      )}

      <section aria-labelledby="my-requests-heading">
        <h2 id="my-requests-heading" className={ui.h2}>
          Your requests
        </h2>
        <ServiceRequestList
          requests={mine}
          empty="You have not asked for a service yet."
          onChanged={updated => {
            replace(setMine)(updated);
            replace(setOrgRequests)(updated);
          }}
        />
      </section>
    </>
  );
}
